import { camelCaseToSentenceCase } from './letter-case.js';

import type { HilSigmaModelDefinitionParameterEntry } from '@cadstrom/backend/shared';

/** How datasheets refer to a sigma model parameter, where its name alone does not say. */
interface SigmaParameterDatasheetTerms {
	/** Sentence-case label to show in place of the one derived from the parameter name. */
	label?: string;
	/** Symbols datasheets print for the parameter. */
	symbols: string[];
}

/** Datasheet terms by parameter name. Every name here belongs to a single sigma model. */
const DATASHEET_TERMS: Readonly<Partial<Record<string, SigmaParameterDatasheetTerms>>> = {
	collectorEmitterMinVoltage: { label: 'Collector-emitter saturation voltage', symbols: ['VCE(sat)'] },
	collectorEmitterMaxVoltage: { symbols: ['VCEO'] },
	collectorBaseVoltage: { symbols: ['VCBO'] },
	emitterBaseVoltage: { symbols: ['VEBO'] },
	baseEmitterSaturationVoltage: { symbols: ['VBE(sat)'] },
};

/**
 * The operator-facing label for a sigma model parameter.
 *
 * @param name - The parameter name.
 * @returns The datasheet label when the name has one, otherwise the name in sentence case.
 */
export function sigmaParameterLabel(name: string): string {
	return DATASHEET_TERMS[name]?.label ?? camelCaseToSentenceCase(name);
}

/**
 * The other names a sigma model parameter goes by, datasheet symbols first.
 *
 * @param parameter - The parameter definition.
 * @returns The distinct other names, or `null` when there are none.
 */
export function sigmaParameterOtherNames(
	parameter: Pick<HilSigmaModelDefinitionParameterEntry, 'name' | 'otherNames'>,
): string | null {
	const otherNames = [...new Set([...(DATASHEET_TERMS[parameter.name]?.symbols ?? []), ...parameter.otherNames])];
	return otherNames.length > 0 ? otherNames.join(', ') : null;
}
